/**
 * verdictLabels — single source of truth on the frontend for
 * rendering the backend's `dual_score.display_verdict` field.
 *
 * Operator brief (Feb 13, 2026 — Phase D trust loop):
 *   "Frontend must NOT reinterpret verdict semantics independently
 *    anymore. Backend becomes the single source of truth for
 *    verdict meaning."
 *
 * Usage:
 *   const v = resolveVerdict(dualScore, reviewIntent);
 *   <span className={`verdict-pill ${v.toneClass}`}>{v.label}</span>
 *
 * Behaviour:
 *   - Primary: read `dualScore.display_verdict`.
 *   - Secondary (legacy backward-compat): when display_verdict is
 *     missing, derive a label from `dualScore.pupil_score.readiness_band`.
 *     This is the ONLY fallback path; it keeps pre-Feb-13 reviews
 *     readable but is not the trust path for new ones.
 *   - `no_evidence` is rendered with neutral styling and the
 *     `no_evidence_reason` string is surfaced to the caller.
 */

// Label per (intent, display_verdict). Lowercase / snake_case keys
// match the backend `_DISPLAY_VERDICT_BY_INTENT` set exactly.
const VERDICT_LABEL = {
  // Mock Test
  likely_pass:  "Likely pass",
  borderline:   "Borderline",
  likely_fail:  "Likely fail",
  // Independent Drive Readiness
  ready:        "Ready",
  developing:   "Developing",
  needs_support:"Needs support",
  // Coaching Review — also uses competent + developing + needs_support
  competent:    "Competent",
  // General Drive
  good_drive:   "Good drive",
  decent_drive: "Decent drive",
  mixed_drive:  "Mixed drive",
  tough_drive:  "Tough drive",
  // PDI / Part 3
  a:            "A",
  b:            "B",
  c:            "C",
  fail:         "Fail",
  // F2 honesty state
  no_evidence:  "No evidence",
};

// Tone palette per verdict — green = strong, sky = OK, amber = work
// needed, rose = serious gap, slate = neutral (no_evidence / unknown).
const TONE_GREEN = "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
const TONE_SKY   = "bg-sky-500/15 text-sky-300 border-sky-500/30";
const TONE_AMBER = "bg-amber-500/15 text-amber-300 border-amber-500/30";
const TONE_ROSE  = "bg-rose-500/15 text-rose-300 border-rose-500/30";
const TONE_SLATE = "bg-slate-500/15 text-slate-300 border-slate-500/30";

const VERDICT_TONE = {
  // strong
  likely_pass:  TONE_GREEN,
  ready:        TONE_GREEN,
  competent:    TONE_GREEN,
  good_drive:   TONE_GREEN,
  a:            TONE_GREEN,
  // mid-strong
  borderline:   TONE_SKY,
  decent_drive: TONE_SKY,
  b:            TONE_SKY,
  // mid-weak
  developing:   TONE_AMBER,
  mixed_drive:  TONE_AMBER,
  c:            TONE_AMBER,
  // weak / failing
  likely_fail:  TONE_ROSE,
  needs_support:TONE_ROSE,
  tough_drive:  TONE_ROSE,
  fail:         TONE_ROSE,
  // honesty
  no_evidence:  TONE_SLATE,
};

// Legacy band → modern verdict fallback. Used ONLY when
// display_verdict is absent on the row (pre-Feb-13 reviews).
const LEGACY_BAND_FALLBACK = {
  test_ready:                 { v: "ready",        intent: "independent_drive" },
  nearly_ready:               { v: "developing",   intent: "independent_drive" },
  needs_guided_development:   { v: "developing",   intent: "independent_drive" },
  not_safe_independently_yet: { v: "needs_support",intent: "independent_drive" },
};

/**
 * Resolve the canonical verdict for one review.
 * @param dualScore  The `dual_score` payload from the API. May be null.
 * @param reviewIntent Optional review_intent for display polish.
 * @returns {{
 *   verdict: string|null,
 *   label: string,
 *   toneClass: string,
 *   isNoEvidence: boolean,
 *   noEvidenceReason: string,
 *   isLegacy: boolean,
 * }}
 */
export function resolveVerdict(dualScore, reviewIntent = null) {
  if (!dualScore) {
    return {
      verdict: null, label: "", toneClass: TONE_SLATE,
      isNoEvidence: false, noEvidenceReason: "", isLegacy: false,
    };
  }
  const dv = (dualScore.display_verdict || "").toString().toLowerCase();
  const isNoEvidence =
    !!dualScore.no_evidence_captured || dv === "no_evidence";
  if (isNoEvidence) {
    return {
      verdict: "no_evidence",
      label: VERDICT_LABEL.no_evidence,
      toneClass: TONE_SLATE,
      isNoEvidence: true,
      noEvidenceReason:
        dualScore.no_evidence_reason ||
        "We couldn't find driving content in this recording.",
      isLegacy: false,
    };
  }
  // Primary path — backend-locked verdict.
  if (dv && VERDICT_LABEL[dv]) {
    return {
      verdict: dv,
      label: VERDICT_LABEL[dv],
      toneClass: VERDICT_TONE[dv] || TONE_SLATE,
      isNoEvidence: false,
      noEvidenceReason: "",
      isLegacy: false,
    };
  }
  // Legacy fallback — derive from readiness_band when display_verdict
  // is missing (pre-Feb-13 reviews). Marked isLegacy so the UI may
  // show a small "legacy review" hint if needed.
  const band = ((dualScore.pupil_score || {}).readiness_band || "").toLowerCase();
  const f = LEGACY_BAND_FALLBACK[band];
  if (f) {
    return {
      verdict: f.v,
      label: VERDICT_LABEL[f.v] || f.v,
      toneClass: VERDICT_TONE[f.v] || TONE_SLATE,
      isNoEvidence: false,
      noEvidenceReason: "",
      isLegacy: true,
    };
  }
  // Nothing usable — render neutral so we never show a confidently
  // wrong label.
  return {
    verdict: null, label: "", toneClass: TONE_SLATE,
    isNoEvidence: false, noEvidenceReason: "", isLegacy: false,
  };
}

// Convenience: pure label lookup for tiny pills that don't need
// the tone class (e.g. /my-training history rows).
export function verdictLabel(verdict) {
  if (!verdict) return "";
  return VERDICT_LABEL[String(verdict).toLowerCase()] || verdict;
}

// Convenience for the no_evidence styling — components without
// access to the full dualScore can ask for the slate tone.
export const VERDICT_TONE_NEUTRAL = TONE_SLATE;
